import { CommonModule, NgIf } from '@angular/common'
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
  Subscription,
  take,
  tap
} from 'rxjs'
import {
  selectCtc,
  selectCtcValue,
  selectRevisedCtc
} from '../../../store/employee-salary/employee-salary.selectors'
import { salaryRevisionActions } from '../../../store/employee-salary/employee-salary.actions'
import { CTC, SalaryMode } from '../../../store/employee-salary/employee-salary.state'
import { IndianNumberInputFormatDirective } from '../../../directives/indian-number-input-format.directive'
import dayjs from 'dayjs'
import { ActivatedRoute } from '@angular/router'
import { selectLatestUserPayrollDate } from '../../../store/payroll/payroll.reducers'
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'app-ctc-revision',
  imports: [
    NgIf,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    IndianNumberInputFormatDirective
  ],
  templateUrl: './ctc-revision.component.html',
  styleUrl: './ctc-revision.component.scss'
})
export class CtcRevisionComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private fb = inject(FormBuilder)
  form!: FormGroup
  currentMode!: SalaryMode
  salaryMode = SalaryMode
  minPayOutFromMonth$!: Observable<string>
  minEffectiveFromDate!: string
  latestPayrollDate$ = this.store.select(selectLatestUserPayrollDate)
  //Following "currentCtcValue" is the value of user's current CTC
  //and will be fetched from the store
  //CTC value inserted in this component's template is the value of user's revised CTC
  currentCtcValue!: number
  @Output() formValuesChanged = new EventEmitter<CTC>()
  private subscription!: Subscription

  ngOnInit(): void {
    this.currentMode = this.route.snapshot.data['mode'] as SalaryMode
    this.initializeForm()
    this.setupMinimumPayoutFromMonth()

    // Emit live ctc value for Parent Component
    this.subscription =
      this.form.valueChanges.subscribe(value => {
        this.formValuesChanged.emit(value)
      }) ?? new Subscription()

    this.store
      .select(selectCtcValue)
      .pipe(take(1))
      .subscribe(ctcValue => {
        this.currentCtcValue = ctcValue
      })

    this.store
      .select(selectCtc)
      .pipe(take(1))
      .subscribe(ctc => {
        if (ctc.effectiveFrom) {
          //if (this.currentMode === SalaryMode.REVISE) {
          this.minEffectiveFromDate = dayjs(ctc.effectiveFrom)
            .add(1, 'month')
            .startOf('month')
            .format('YYYY-MM-DD')
          //this.form.patchValue({ effectiveFrom: this.minEffectiveFromDate }, { emitEvent: true })
          //} else this.minEffectiveFromDate = ctc.effectiveFrom
        }
      })

    localStorage.setItem('currentCtc', this.currentCtcValue.toString())

    this.store
      .select(selectRevisedCtc)
      .pipe(take(1))
      .subscribe(ctc => {
        if (ctc) {
          if (ctc.effectiveFrom && this.currentMode === SalaryMode.REVISE) {
            if (+this.currentCtcValue !== ctc.ctc) {
              this.form.patchValue(
                { ctc: ctc.ctc, effectiveFrom: ctc.effectiveFrom, payoutFrom: ctc.payoutFrom },
                { emitEvent: true }
              )
            } else {
              this.form.patchValue(
                { ctc: ctc.ctc, effectiveFrom: '', payoutFrom: '' },
                { emitEvent: true }
              )
            }
          } else if (ctc.effectiveFrom && this.currentMode === SalaryMode.UPDATE) {
            this.form.patchValue(
              { ctc: ctc.ctc, effectiveFrom: ctc.effectiveFrom, payoutFrom: ctc.payoutFrom },
              { emitEvent: true }
            )
          } else this.form.patchValue(ctc, { emitEvent: true })
        }
      })
  }

  ngOnDestroy() {
    this.store.dispatch(salaryRevisionActions.updateCTC({ ctc: this.form.value }))
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  initializeForm() {
    this.form = this.fb.group({
      basis: ['annual', Validators.required],
      currency: ['INR', Validators.required],
      ctc: [0, Validators.min(1)],
      effectiveFrom: ['', Validators.required],
      payoutFrom: ['', Validators.required]
    })
  }

  setupMinimumPayoutFromMonth() {
    this.minPayOutFromMonth$ = combineLatest([
      this.form.get('effectiveFrom')!.valueChanges.pipe(
        //startWith(this.form.get('effectiveFrom')?.value ?? this.minEffectiveFromDate), // Emit initial value
        map(effectiveDateValue => {
          const dateStringToUse = effectiveDateValue ?? this.minEffectiveFromDate
          if (dateStringToUse) {
            const dateObj = new Date(dateStringToUse)
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toISOString().slice(0, 7)
            }
          }
          return new Date().toISOString().slice(0, 7)
        })
      ),
      this.latestPayrollDate$.pipe(startWith(null))
    ]).pipe(
      map(([effectiveFromMonth, latestPayrollMonth]) => {
        if (latestPayrollMonth) {
          return effectiveFromMonth > latestPayrollMonth ? effectiveFromMonth : latestPayrollMonth
        } else {
          return effectiveFromMonth
        }
      }),
      distinctUntilChanged(),
      tap(minMonth => {
        const payoutFromControl = this.form.get('payoutFrom')
        const currentPayoutValue = payoutFromControl?.value

        if (!currentPayoutValue || currentPayoutValue < minMonth) {
          payoutFromControl?.patchValue(minMonth, { emitEvent: true })
        } else {
          payoutFromControl?.updateValueAndValidity({ emitEvent: false })
        }
      })
    )
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  updateCtcAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = Number(inputElement.value)
    if (isNaN(percentage)) return

    const currentCtc = Number(this.currentCtcValue) || 0
    const increaseAmount = (currentCtc * percentage) / 100
    const revisedCtc = currentCtc + increaseAmount
    const finalCtc = Math.round(revisedCtc)

    this.form.patchValue({ ctc: finalCtc })
    // ✅ Save current CTC in localStorage
    // localStorage.setItem('currentCtc', currentCtc.toString())
  }
}
